import { useState } from "react";
import ControlSlider from "./control-slider";
import { CONTROL_SLIDER_MARKER_PCTS } from "./slider-math";
import SegmentedControl, { SegmentedToggle } from "./segmented-control";
import SlotWord from "./slot-word";
import useSpeedLabel from "./speed-label";

// Normal: the slider runs 1 → 1.4 and maps to slowControl 1 → 0.6 (slower).
// With Fast Control on, the same travel maps to 1 → 1.4, so the control speeds
// the video up instead. Either way the far end of the slider is "more effect",
// so the track and markers stay put.
//
// The title names the direction ("Slow" rolls to "Fast"). The far end of its
// row holds Reset all (see reset-all.js), a
// Slow | Fast segmented control and a Pitch toggle that reads "Pitch locked"
// when on. Fast and Pitch Lock are Pro, so while Pro is locked they open the
// upgrade instead.
// Reset all comes out when effects call for it, then stays until every
// control is back at its default (by hand or through Reset all itself).
function useStickyResetAll(wanted, atDefaults) {
  const [shown, setShown] = useState(wanted);
  if (wanted && !shown) setShown(true);
  if (shown && !wanted && atDefaults) setShown(false);
  return shown;
}

export default function SlowControlSection({
  slowControl,
  speedUpMode = false,
  pitchLocked = false,
  isProFeaturesLocked = false,
  showReset = false,
  controlsAtDefaults = true,
  onSlowControlChange,
  onPitchLockChange,
  onSpeedUpModeChange,
  onResetClick,
  onUpgradeClick,
}) {
  const locked = !!isProFeaturesLocked;
  // Never on while locked, even if it was left on before.
  const fast = !locked && !!speedUpMode;
  const pitch = !locked && !!pitchLocked;
  const { label, gliding } = useSpeedLabel(fast, slowControl);
  const resetShown = useStickyResetAll(showReset, controlsAtDefaults);
  const sliderValue = fast ? slowControl : 2 - slowControl;

  return (
    <div className="content__item content__item--slow">
      <div className="content__title">
        <span>
          <SlotWord word={fast ? "Fast" : "Slow"} /> Control
        </span>
        <div className="content__controls">
          <button
            type="button"
            className={`content__link content__reset${
              resetShown ? "" : " content__hidden"
            }`}
            title="Put every control back to its default"
            onClick={() => onResetClick?.()}
          >
            Reset all
          </button>
          <SegmentedControl
            label="Direction"
            value={fast}
            options={[
              { value: false, label: "Slow" },
              {
                value: true,
                label: "Fast",
                title: locked ? "Fast Control is a Pro feature" : undefined,
              },
            ]}
            onChange={(next) =>
              locked ? onUpgradeClick?.() : onSpeedUpModeChange?.(next)
            }
          />
          <SegmentedToggle
            label={<SlotWord word={pitch ? "Pitch locked" : "Pitch"} />}
            pressed={pitch}
            title={locked ? "Pitch Lock is a Pro feature" : undefined}
            onChange={(next) =>
              locked ? onUpgradeClick?.() : onPitchLockChange?.(next)
            }
          />
        </div>
      </div>
      <ControlSlider
        label={label}
        gliding={gliding}
        markerPcts={CONTROL_SLIDER_MARKER_PCTS}
        min="1"
        max="1.4"
        step="0.01"
        value={sliderValue}
        ghostEnabled
        onChange={(event) => {
          const raw = parseFloat(event.currentTarget.value);
          const next = fast ? raw : 2 - raw;
          onSlowControlChange(parseFloat(next.toFixed(2)));
        }}
      />
    </div>
  );
}
